import { Trans, useTranslation } from 'react-i18next';
import { Button } from '@/components/ui';

/**
 * EAA-1817: the "Không lưu được" recovery card (frame 51's red card) for a
 * create/re-point write that failed because the app's live catalog no longer
 * has the `app_role` being written — shared by `RoleMapAddModal` (create) and
 * `RoleMapRepointModal` (re-point) so the two write paths cannot drift apart
 * on copy or behaviour.
 *
 * This is deliberately NOT a toast: `useToast()`'s `ToastApi` has no slot for
 * two action buttons (see `useRoleMapWrites.ts`'s "Hoàn tác" docstring for the
 * same contract gap), and the design shows this as a persistent inline card
 * with its own icon/title/body, not a transient notification anyway — an
 * operator mid-edit needs it to stay until they act, not auto-dismiss.
 */
export function RoleMapStaleCatalogCard({
    appName,
    appRole,
    onSync,
    syncBusy,
    onRetry,
}: {
    /** Display name (or code, if unknown) of the app whose catalog moved. */
    appName: string;
    appRole: string;
    onSync: () => void;
    syncBusy: boolean;
    /** Omit when there is nothing left to resubmit (e.g. the stale role was the
     *  only one picked, and removing it left an empty selection). */
    onRetry?: () => void;
}) {
    const { t } = useTranslation();
    return (
        <div className="rm-stale-card" role="alert">
            <span className="rm-stale-card__icon" aria-hidden="true">
                !
            </span>
            <div className="rm-stale-card__body">
                <strong className="rm-stale-card__title">
                    {t('console.roleMap.staleCatalogCard.title')}
                </strong>
                <p className="rm-stale-card__text">
                    <Trans
                        i18nKey="console.roleMap.staleCatalogCard.text"
                        values={{ appName, appRole }}
                        components={[<code key="role" />]}
                    />
                </p>
                <div className="rm-stale-card__actions">
                    <Button className="rm-stale-card__sync" onClick={onSync} disabled={syncBusy}>
                        {syncBusy
                            ? t('console.roleMap.staleCatalogCard.syncing')
                            : t('console.roleMap.staleCatalogCard.syncCatalog')}
                    </Button>
                    {onRetry && (
                        <Button className="rm-stale-card__retry" onClick={onRetry}>
                            {t('console.roleMap.staleCatalogCard.retry')}
                        </Button>
                    )}
                </div>
            </div>
        </div>
    );
}
